// import { SortableElement } from 'react-sortable-hoc';

import React, { useContext } from 'react';
import { Animations } from 'appkit-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ANIMATION_TYPES } from './config';
import { ConfigContext } from './formList';

import { renderCells } from './cell';
import { flexStyle } from '@modules/projects/projectsForm/customizeModule/manageMForm/config/columns.config';

const { ADD, DELETE } = ANIMATION_TYPES;

const checkAnimation =
  (animation = {}, rowKey) =>
  (type) =>
    animation.id === rowKey && animation.action === type;

const getAnimation = (...args) => {
  const check = checkAnimation(...args);
  if (check(DELETE)) {
    return 'fadeOut';
  }
  if (check(ADD)) {
    return 'bounceInRight';
  }

  return null;
};

function FormRow(props) {
  const {
    rowKey,
    isActive,
    animation,
    name,
    extraForm,
    formInstance,
    projHomeExtraForm,
    multiSectionExtraForm,
    sortableId,
  } = props;

  const { onClick, clearAnimation, afterDeleteAnimation } = props;
  const {
    columns = [],
    defaultColumns = [],
    ...restConfig
  } = useContext(ConfigContext);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId || rowKey, // Fallback to rowKey if sortableId not provided
  });

  const style = {
    // ✅ Always apply transform for smooth movement
    transform: CSS.Transform.toString(transform),
    transition, // ✅ Keep dnd-kit's built-in transitions
    // ✅ Make original item semi-transparent during drag, not invisible
    opacity: isDragging ? 0.3 : 1,
    // ✅ Keep original item visible but indicate it's being dragged
    position: 'relative',
    zIndex: isDragging ? 0 : 1, // Lower z-index for original during drag
  };
  const ANIMATION = getAnimation(animation, name + rowKey);

  const afterA = () => {
    if (animation.action === DELETE) {
      afterDeleteAnimation &&
        afterDeleteAnimation(rowKey, props.index ?? props.field?.name);
    }
    if (ANIMATION) {
      clearAnimation && clearAnimation();
    }
  };
  const clickTrigger = (e) => {
    // Don't trigger if clicking on form inputs or drag handle
    if (
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.tagName === 'SELECT' ||
      e.target.closest('.drag-handle')
    ) {
      return;
    }
    onClick && onClick(rowKey);
  };

  let _columns = [];
  _columns = extraForm ? [...columns, extraForm] : columns;

  const customizeModue = formInstance.getFieldValue('customizeModule');
  const projectHomeTmplt = formInstance.getFieldValue('projectHomeTmplt');
  const rowIndex = props.index ?? props.field?.name;

  if (customizeModue && customizeModue[rowIndex]?.type === 'default') {
    _columns = defaultColumns;
  } else if (
    !projHomeExtraForm &&
    projectHomeTmplt &&
    projectHomeTmplt[rowIndex]?.type === 'default'
  ) {
    _columns = defaultColumns;
  } else if (multiSectionExtraForm) {
    _columns = _columns.map((item) => {
      if (item?.formConfig?.inputType === 'delete') {
        item.style = {
          ...flexStyle,
          width: '10rem',
        };
      }
      return item;
    });
  }
  const cells = _columns.map((column, index) => {
    try {
      return renderCells({ ...props, ...restConfig })(column, index);
    } catch (error) {
      console.warn('Error rendering cell:', error, { column, rowKey });
      return <div key={`error-${index}`}>Error rendering cell</div>;
    }
  });

  // In FormRow, make sure your drag handle looks like this:
  const dragHandle = (
    <div
      className="drag-handle"
      {...listeners} // ✅ Critical
      {...attributes} // ✅ Critical
      style={{
        cursor: 'grab',
        padding: '8px',
        // backgroundColor: 'rgba(255, 0, 0, 0.2)', // ✅ Temporary background to see it
        // border: '2px solid red', // ✅ Temporary border
        position: 'absolute',
        left: '0',
        top: '0',
        zIndex: 10,
        width: '40px',
        height: '40px',
        touchAction: 'none',
      }}
      onMouseDown={() => console.log('🖱️ Drag handle clicked')}
    >
      <span className="appkiticon icon-drag" />
    </div>
  );

  // Enhanced injection - try multiple approaches
  let injectionSuccess = false;

  // Approach 1: Try to find drag cell
  const dragCellIndex = _columns.findIndex(
    (col) => col.key && col.key.startsWith('drag-'),
  );

  if (dragCellIndex !== -1 && cells[dragCellIndex]) {
    try {
      const originalCell = cells[dragCellIndex];
      cells[dragCellIndex] = (
        <div
          key={`drag-wrapper-${dragCellIndex}`}
          style={{
            position: 'relative',
            ...originalCell.props?.style,
          }}
        >
          {dragHandle}
          {originalCell}
        </div>
      );
      injectionSuccess = true;
      console.log('✅ Injected into drag cell at index:', dragCellIndex);
    } catch (error) {
      console.warn('❌ Failed to inject into drag cell:', error);
    }
  }

  // Approach 2: Fallback to first cell if drag cell injection failed
  if (!injectionSuccess && cells.length > 0) {
    try {
      const firstCell = cells[0];
      cells[0] = (
        <div
          key="first-cell-wrapper"
          style={{
            position: 'relative',
            ...firstCell.props?.style,
          }}
        >
          {dragHandle}
          {firstCell}
        </div>
      );
      injectionSuccess = true;
      console.log('✅ Injected into first cell');
    } catch (error) {
      console.warn('❌ Failed to inject into first cell:', error);
    }
  }

  // Approach 3: Last resort - add as separate element
  if (!injectionSuccess) {
    console.log('⚠️ Using fallback: Adding drag handle as separate element');
    cells.unshift(
      <div
        key="standalone-drag-handle"
        style={{ position: 'relative', width: '40px' }}
      >
        {dragHandle}
      </div>,
    );
  }

  return (
    <Animations
      effect={ANIMATION}
      active={Boolean(ANIMATION)}
      afterAnimation={afterA}
    >
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        // {...listeners}
        onClick={clickTrigger}
        data-sortable-id={sortableId}
        className={`dragable-table-form-row ${isActive ? 'is-active' : ''} ${
          isDragging ? 'is-dragging' : ''
        }`}
        onKeyDown={(e) => {
          // Allow keyboard input without interference
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
            e.stopPropagation();
          }
        }}
      >
        {cells}
      </div>
    </Animations>
  );
}

export default FormRow;
